/* Founder Focus mode — what to do today. */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";

interface Lead {
  id: string;
  company_name: string;
  pipeline_stage: string;
  target_contact_name?: string;
  target_role?: string;
  lead_score?: number;
  updated_at: number;
}

const STAGE_BUCKETS: Record<string, string[]> = {
  "Top leads to contact": ["qualified", "approved_for_outreach"],
  "Follow-ups due":       ["follow_up_due"],
  "Replies awaiting action": ["replied"],
  "Calls":                ["call_booked", "demo_completed"],
  "Pilots at risk":       ["pilot_offered", "trial_active"],
};

export function GrowthDashboard() {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.listGrowthLeads({});
        setLeads((r.leads as Lead[]) ?? []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const buckets = useMemo(() => {
    const out: Record<string, Lead[]> = {};
    for (const [label, stages] of Object.entries(STAGE_BUCKETS)) {
      const matched = (leads ?? [])
        .filter((l) => stages.includes(l.pipeline_stage))
        .sort((a, b) => (b.lead_score ?? 0) - (a.lead_score ?? 0))
        .slice(0, label.startsWith("Top leads") ? 10 : 8);
      out[label] = matched;
    }
    return out;
  }, [leads]);

  const totals = useMemo(() => {
    const t = { total: 0, qualified: 0, contacted: 0, customers: 0 };
    for (const l of leads ?? []) {
      t.total++;
      if (l.pipeline_stage === "qualified" || l.pipeline_stage === "approved_for_outreach") t.qualified++;
      if (l.pipeline_stage === "contacted" || l.pipeline_stage === "follow_up_due") t.contacted++;
      if (l.pipeline_stage === "customer" || l.pipeline_stage === "paid_pilot") t.customers++;
    }
    return t;
  }, [leads]);

  if (loading) return <div className="sg-empty">Loading…</div>;

  return (
    <div className="sg-growth-dashboard">
      <div className="sg-growth-kpis">
        <Kpi label="Leads" value={totals.total} />
        <Kpi label="Qualified" value={totals.qualified} />
        <Kpi label="Contacted" value={totals.contacted} />
        <Kpi label="Customers" value={totals.customers} accent />
      </div>

      <div className="sg-growth-focus">
        {Object.entries(buckets).map(([label, items]) => (
          <section key={label} className="sg-growth-focus-section">
            <h3 className="sg-growth-focus-title">{label}</h3>
            {items.length === 0 ? (
              <div className="sg-growth-focus-empty">Nothing here. Good.</div>
            ) : (
              <ul className="sg-growth-focus-list">
                {items.map((l) => (
                  <li key={l.id}>
                    <Link to={`/admin/growth/leads?focus=${l.id}`}>
                      <strong>{l.company_name}</strong>
                      {l.target_contact_name && (
                        <span className="sg-muted">  · {l.target_contact_name}{l.target_role ? `, ${l.target_role}` : ""}</span>
                      )}
                      {typeof l.lead_score === "number" && (
                        <span className="sg-growth-score">  Score {l.lead_score}/10</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`sg-growth-kpi${accent ? " is-accent" : ""}`}>
      <div className="sg-growth-kpi-value">{value}</div>
      <div className="sg-growth-kpi-label">{label}</div>
    </div>
  );
}
